import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { capturaPeriodStatus, kpiMatrix, suppliers as suppliersApi } from "@/api";
import http from "@/api";
import { adminPeriodNotice, clampPeriod, initialPeriod, maxPeriod } from "@/lib/capturePeriod";
import { PILLARS } from "@/constants";
import { useCategories } from "@/hooks/useCategories";
import { RubricPanel } from "@/components/RubricPanel";
import PlantAverageReal from "@/components/PlantAverageReal";
import { levelGap, plantRealView } from "@/lib/plantReal";
import {
  ArrowLeft, Calendar, ChevronDown, ChevronRight, Search, Target, Activity,
  CheckCircle2, Save, AlertTriangle, Users,
} from "lucide-react";

const LEVEL_COLORS = { 1: "#E0162B", 2: "#F59E0B", 3: "#3B82F6", 4: "#10B981" };
const LEVEL_LABEL = { 1: "1 · Inicial", 2: "2 · En Desarrollo", 3: "3 · Competente", 4: "4 · Elite" };
const PILLAR_LABEL = Object.fromEntries(PILLARS.map((p) => [p.key, p.label]));

const PERIODICITY_MONTHS = { bimestral: [2, 4, 6, 8, 10, 12], trimestral: [3, 6, 9, 12], semestral: [3, 9], anual: [12] };
const PERIODICITY_LABEL = { mensual: "Mensual", bimestral: "Bimestral", trimestral: "Trimestral", semestral: "Semestral", anual: "Anual" };

// Borrador local de capturas sin guardar: si la sesión expira (401) el usuario es enviado
// a login y al volver recupera lo que había capturado. Solo vive en este navegador.
const DRAFT_KEY = "lala:captura-draft";

function saveDraft(period, dirty) {
  try {
    if (Object.keys(dirty).length === 0) localStorage.removeItem(DRAFT_KEY);
    else localStorage.setItem(DRAFT_KEY, JSON.stringify({ period, dirty }));
  } catch (_) { /* storage no disponible: el borrador es opcional */ }
}

function loadDraft(period) {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
    return d && d.period === period && d.dirty ? d.dirty : {};
  } catch (_) {
    return {};
  }
}

// Compara sin acentos, mayúsculas ni espacios de más: las categorías y nombres que llegan por
// carga masiva no siempre coinciden letra por letra con la lista configurada por el admin.
const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase();

// Filtros de categoría y búsqueda (nombre o SAP ID); se usan en ambas vistas.
function matchesFilters(s, category, q) {
  if (category !== "all" && norm(s.category) !== norm(category)) return false;
  const nq = norm(q);
  if (nq && !norm(s.name).includes(nq) && !norm(s.sap_id).includes(nq)) return false;
  return true;
}

function periodicityApplies(periodicity, period) {
  const months = PERIODICITY_MONTHS[(periodicity || "mensual").toLowerCase()];
  if (!months) return true;
  const m = Number((period || "").split("-")[1]);
  if (!m) return true;
  return months.includes(m);
}

/**
 * Captura Mensual — vista fluida para el evaluador por pilar.
 * En vez de una matriz densa, muestra cards colapsables por proveedor.
 * Cada card lista sus indicadores con dropdowns 1–4 grandes con rúbricas visibles.
 * Autosave cuando el evaluador cambia el nivel · botón "Guardar todo" al final.
 */
export default function CapturaMensual() {
  // El periodo inicial lo decide el servidor (RF-3): hasta que responda no se pinta la pantalla.
  // Si no responde, se usa el mes del navegador sin límites: el servidor valida cada guardado igual (RF-6).
  const [status, setStatus] = useState(undefined); // undefined = cargando, null = no disponible
  useEffect(() => {
    let alive = true;
    capturaPeriodStatus()
      .then((s) => { if (alive) setStatus(s); })
      .catch(() => { if (alive) setStatus(null); });
    return () => { alive = false; };
  }, []);
  if (status === undefined) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-10 text-sm text-[var(--ink-soft)]" data-testid="cm-period-loading">
        Cargando periodo…
      </div>
    );
  }
  return <CapturaMensualBody initialStatus={status} />;
}

function CapturaMensualBody({ initialStatus }) {
  const [suppliers, setSuppliers] = useState([]);
  const CATEGORIES = useCategories();
  const [mode, setMode] = useState("proveedor");
  const [selected, setSelected] = useState({});          // {sid: true}
  const [expanded, setExpanded] = useState({});          // {sid: true}
  const [matrix, setMatrix] = useState({});              // {sid: data from api}
  const [dirty, setDirty] = useState({});                // {sid::ind: {objetivo_level, real_level, notes, period}}
  const [savedAt, setSavedAt] = useState({});            // {sid::ind: ts}
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(initialStatus);  // estado del bloqueo (RF-3..RF-5, RF-14)
  const [period, setPeriodRaw] = useState(() => initialPeriod(initialStatus));
  const max = maxPeriod(status);                          // solo el evaluador y el comprador tienen tope (RF-4)
  const setPeriod = (value) => setPeriodRaw(clampPeriod(value, max));
  // Si el tope baja (otra sesión, un cambio del admin), el periodo vuelve a él.
  useEffect(() => { setPeriodRaw((p) => clampPeriod(p, max)); }, [max]);
  const lockedMode = initialStatus?.mode === "evaluator";
  // Repite la consulta tras guardar o rechazar para que el desbloqueo se vea sin recargar (RF-7).
  const refreshStatus = async () => {
    if (!lockedMode) return;
    try { setStatus(await capturaPeriodStatus()); } catch (_) { /* se queda el último estado conocido */ }
  };
  const [busy, setBusy] = useState(false);
  const [globalMsg, setGlobalMsg] = useState("");
  const [err, setErr] = useState("");
  const debounceRef = useRef({}); // {sid::ind: timeoutId}
  const dirtyRef = useRef({});    // espejo síncrono de dirty (evita stale closure en autosave)
  const inFlightRef = useRef({}); // {sid::ind: true} guardado en vuelo (evita peticiones solapadas)
  const matrixReqRef = useRef({}); // {sid: n} último request de matriz (descarta respuestas viejas)
  const [scope, setScope] = useState({ role: null, assigned_pillars: [], plant: null });
  const [plantSel, setPlantSel] = useState("");
  const [plantList, setPlantList] = useState([]);
  // Recupera capturas pendientes de una sesión anterior (mismo periodo).
  useEffect(() => {
    const draft = loadDraft(period);
    const n = Object.keys(draft).length;
    if (n === 0) return;
    dirtyRef.current = { ...draft, ...dirtyRef.current };
    setDirty(dirtyRef.current);
    setGlobalMsg(`Se recuperaron ${n} captura(s) sin guardar. Usa "Guardar todo" para enviarlas.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    http.get("/plantas").then((r) => setPlantList(r.data.plantas || [])).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await suppliersApi.list();
        const arr = res.items || res;
        setSuppliers(arr);
        try {
          const s = await http.get("/me/scope").then((r) => r.data);
          setScope({ role: s.role, assigned_pillars: s.assigned_pillars || [], plant: s.plant || null });
          if (s.role === "evaluator" && (s.assigned_pillars || []).length > 0) setMode("pilar");
        } catch (_) {}
      } catch (e) {
        setErr(e.response?.data?.detail || e.message);
      }
    })();
  }, []);

  useEffect(() => {
    setMatrix({});
    const open = Object.keys(expanded).filter((k) => expanded[k]);
    open.forEach(async (sid) => {
      try {
        const res = await kpiMatrix.get(sid, period);
        setMatrix((prev) => ({ ...prev, [sid]: res }));
      } catch (_) {}
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const canCaptureAllPillars = scope.role === "admin" || !(scope.assigned_pillars && scope.assigned_pillars.length);
  const isProposer = false;
  const allowedPillars = scope.assigned_pillars || [];

  const filtered = useMemo(
    () => suppliers.filter((s) => matchesFilters(s, category, q)),
    [suppliers, category, q],
  );

  // Categorías configuradas + las que traen los proveedores visibles y no están en la lista,
  // para que ningún proveedor quede fuera del filtro.
  const categoryOptions = useMemo(() => {
    const seen = new Set(CATEGORIES.map(norm));
    const extra = [];
    suppliers.forEach((s) => {
      if (s.category && !seen.has(norm(s.category))) {
        seen.add(norm(s.category));
        extra.push(s.category);
      }
    });
    return [...CATEGORIES, ...extra];
  }, [CATEGORIES, suppliers]);

  const loadMatrix = async (sid, force = false) => {
    if (matrix[sid] && !force) return;
    const reqId = (matrixReqRef.current[sid] || 0) + 1;
    matrixReqRef.current[sid] = reqId;
    try {
      const res = await kpiMatrix.get(sid, period);
      if (matrixReqRef.current[sid] !== reqId) return; // llegó una respuesta más nueva
      setMatrix((prev) => ({ ...prev, [sid]: res }));
    } catch (e) {
      setErr(`No se pudo cargar KPIs de ${sid}: ${e.response?.data?.detail || e.message}`);
    }
  };

  const toggleExpand = async (sid) => {
    const isOpen = expanded[sid];
    setExpanded((prev) => ({ ...prev, [sid]: !isOpen }));
    if (!isOpen) await loadMatrix(sid);
  };

  const selectAllVisible = (val) => {
    const next = { ...selected };
    filtered.forEach((s) => { next[s.id] = val; });
    setSelected(next);
    if (val) {
      const nextExpanded = { ...expanded };
      filtered.forEach((s) => { nextExpanded[s.id] = true; loadMatrix(s.id); });
      setExpanded(nextExpanded);
    }
  };

  // Quita de `dirty` solo lo que se envió y sigue igual: lo tecleado mientras la
  // petición estaba en vuelo se conserva. Devuelve true si quedan cambios en la celda.
  const pruneSent = (key, sent) => {
    const cur = dirtyRef.current[key];
    if (!cur) return false;
    const rest = {};
    Object.keys(cur).forEach((f) => {
      if (!(f in sent) || cur[f] !== sent[f]) rest[f] = cur[f];
    });
    const next = { ...dirtyRef.current };
    const remaining = Object.keys(rest).length > 0;
    if (remaining) next[key] = rest; else delete next[key];
    dirtyRef.current = next;
    setDirty(next);
    saveDraft(period, next);
    return remaining;
  };

  const persistCell = async (sid, indicator_code) => {
    const key = `${sid}::${indicator_code}`;
    const payload = dirtyRef.current[key];
    if (!payload) return;
    if (inFlightRef.current[key]) return; // se reprograma al terminar el guardado en vuelo
    inFlightRef.current[key] = true;
    let remaining = false;
    try {
      const res = await kpiMatrix.batch([{ supplier_id: sid, indicator_code, period, ...(plantSel ? { plant: plantSel } : {}), ...payload }]);
      if (res.errors?.length) {
        const first = res.errors[0];
        setErr(`Error en ${indicator_code}: ${first.reason || "no se guardó"}`);
        refreshStatus();
        return;
      }
      remaining = pruneSent(key, payload);
      refreshStatus();
      setSavedAt((prev) => ({ ...prev, [key]: Date.now() }));
      loadMatrix(sid, true);
      setTimeout(() => setSavedAt((prev) => {
        const c = { ...prev };
        if (c[key] && Date.now() - c[key] > 1400) delete c[key];
        return c;
      }), 1600);
    } catch (e) {
      setErr(`Error guardando ${indicator_code}: ${e.response?.data?.detail || e.message}. El cambio queda pendiente — usa "Guardar todo" para reintentar.`);
      if (e.response?.status === 409) refreshStatus();
    } finally {
      inFlightRef.current[key] = false;
      if (remaining) scheduleAutosave(sid, indicator_code);
    }
  };

  // Al ocultar/cerrar la pestaña se intenta guardar lo pendiente sin esperar al debounce.
  // El borrador en localStorage queda como respaldo si la petición no alcanza a salir.
  const flushRef = useRef(() => {});
  flushRef.current = () => {
    Object.keys(dirtyRef.current).forEach((key) => {
      const [sid, code] = key.split("::");
      persistCell(sid, code);
    });
  };
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") flushRef.current(); };
    const onPageHide = () => flushRef.current();
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  const scheduleAutosave = (sid, indicator_code) => {
    const key = `${sid}::${indicator_code}`;
    if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
    debounceRef.current[key] = setTimeout(() => {
      persistCell(sid, indicator_code);
    }, 600);
  };

  const editCell = (sid, indicator_code, field, value) => {
    const key = `${sid}::${indicator_code}`;
    dirtyRef.current = {
      ...dirtyRef.current,
      [key]: { ...(dirtyRef.current[key] || {}), [field]: value },
    };
    setDirty(dirtyRef.current);
    saveDraft(period, dirtyRef.current);
    scheduleAutosave(sid, indicator_code);
  };

  const saveAll = async () => {
    const items = [];
    const sentByKey = {};
    for (const [key, payload] of Object.entries(dirtyRef.current)) {
      const [sid, indicator_code] = key.split("::");
      sentByKey[key] = payload;
      items.push({ supplier_id: sid, indicator_code, period, ...(plantSel ? { plant: plantSel } : {}), ...payload });
    }
    if (items.length === 0) {
      setGlobalMsg("No hay cambios pendientes.");
      setTimeout(() => setGlobalMsg(""), 1800);
      return;
    }
    setBusy(true); setErr("");
    try {
      const res = await kpiMatrix.batch(items);
      const failedKeys = new Set((res.errors || []).map((e) => `${e.supplier_id}::${e.indicator_code}`));
      // Solo se limpia lo enviado y guardado; lo editado durante la petición se conserva.
      Object.keys(sentByKey).forEach((k) => {
        if (!failedKeys.has(k)) pruneSent(k, sentByKey[k]);
      });
      [...new Set(items.map((i) => i.supplier_id))].forEach((sid) => loadMatrix(sid, true));
      const parts = [];
      if (res.saved) parts.push(`Guardado: ${res.saved}`);
      if (res.proposed) parts.push(`Cambios de Objetivo enviados al admin: ${res.proposed}`);
      if (!parts.length) parts.push("Guardado: 0");
      setGlobalMsg(`${parts.join(" · ")} · errores: ${res.errors?.length || 0}`);
      if (res.errors?.length) {
        const first = res.errors[0];
        setErr(`Error en ${first.indicator_code}: ${first.reason || "no se guardó"}${res.errors.length > 1 ? ` (+${res.errors.length - 1} más)` : ""}`);
      }
      setTimeout(() => setGlobalMsg(""), 3500);
      refreshStatus();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      if (e.response?.status === 409) refreshStatus();
    } finally {
      setBusy(false);
    }
  };

  const pendingCount = Object.keys(dirty).length;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="captura-mensual-page">
      <Link to="/dashboard" className="text-sm text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Volver al dashboard
      </Link>

      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs" data-testid="cm-scale-legend">
        <span className="font-bold text-[var(--lala-blue)] uppercase tracking-wider">Escala de captura:</span>
        {[[1, "Inicial"], [2, "En Desarrollo"], [3, "Competente"], [4, "Elite"]].map(([n, lbl]) => (
          <span key={n} className="px-2.5 py-1 rounded-full bg-white border border-[var(--line)] font-semibold"
            data-testid={`cm-scale-${n}`}>
            <span className="text-[var(--lala-blue)] font-display">{n}</span> · {lbl}
          </span>
        ))}
        <span className="text-[var(--ink-soft)] italic">Cada KPI muestra el concepto de cada nivel al desplegar Objetivo / Real.</span>
      </div>

      <div className="mb-6">
        <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
          <Calendar size={18} /> captura mensual
        </div>
        <h1 className="font-display text-4xl">Captura mensual del evaluador</h1>
        <p className="text-[var(--ink-soft)] mt-1 max-w-3xl">
          Elige a tus proveedores, expande sus tarjetas y captura el nivel <b>Real</b> por indicador (1–4).
          Todo se guarda automáticamente. Al terminar, presiona <b>Guardar todo</b> para confirmar la sesión.
        </p>
      </div>

      <div className="flex gap-2 mb-4" data-testid="cm-view-toggle">
        <button onClick={() => setMode("proveedor")}
          className={`sketch-pill px-4 py-1.5 text-xs font-semibold ${mode === "proveedor" ? "bg-[var(--lala-blue)] text-[var(--cream)]" : "bg-[var(--cream-soft)] text-[var(--lala-blue)] border border-[var(--lala-blue)]/40"}`}
          data-testid="cm-mode-proveedor">Por proveedor</button>
        <button onClick={() => setMode("pilar")}
          className={`sketch-pill px-4 py-1.5 text-xs font-semibold ${mode === "pilar" ? "bg-[var(--lala-blue)] text-[var(--cream)]" : "bg-[var(--cream-soft)] text-[var(--lala-blue)] border border-[var(--lala-blue)]/40"}`}
          data-testid="cm-mode-pilar">Por pilar</button>
      </div>

      {status?.mode === "evaluator" && status.message && (
        <div className="mb-5 sketch-border-soft bg-amber-50 border border-amber-300 p-3 text-sm flex items-start gap-2"
          role="status" data-testid="cm-lock-notice">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-700" /> <span>{status.message}</span>
        </div>
      )}
      {status?.mode === "admin" && adminPeriodNotice(period) && (
        <div className="mb-5 sketch-border-soft bg-[var(--lala-blue-soft)] p-3 text-sm inline-flex items-start gap-2"
          role="status" data-testid="cm-admin-period-notice">
          <Calendar size={16} className="mt-0.5 shrink-0" /> <span>{adminPeriodNotice(period)}</span>
        </div>
      )}

      {isProposer && (
        <div className="mb-5 sketch-border-soft bg-amber-50 border border-amber-300 p-3 text-sm flex items-start gap-2" data-testid="cm-proposal-banner">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <span>
            Tus capturas se envían como <b>propuestas</b>: el administrador debe aceptarlas antes de que
            impacten el score del proveedor. Si una propuesta es rechazada, verás el motivo aquí y podrás volver a proponer.
          </span>
        </div>
      )}

      {scope.plant && (
        <div className="mb-5 sketch-border-soft bg-emerald-50 border border-emerald-300 p-3 text-sm inline-flex items-start gap-2" data-testid="cm-plant-banner">
          <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 pt-1">Tu planta</span>
          <span>
            Evalúas el <b>Nivel de Servicio</b> desde la planta <b className="text-emerald-800">{scope.plant}</b>.
            Tus calificaciones se promedian de forma simple con las de las demás plantas para obtener el Nivel de Servicio del proveedor.
          </span>
        </div>
      )}

      {!scope.plant && plantList.length > 0 && (scope.role === "admin" || !(scope.assigned_pillars || []).length || (scope.assigned_pillars || []).includes("relacion")) && (
        <div className="mb-5 sketch-border-soft bg-emerald-50 border border-emerald-300 p-3 text-sm flex flex-wrap items-center gap-3" data-testid="cm-plant-selector-wrap">
          <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Nivel de Servicio · Planta</span>
          <select value={plantSel} onChange={(e) => setPlantSel(e.target.value)}
            className="rounded-lg border border-emerald-300 bg-white px-2 py-1.5 text-sm"
            data-testid="cm-plant-selector">
            <option value="">Sin planta (calificación general)</option>
            {plantList.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <span className="text-xs text-[var(--ink-soft)]">
            Elige la planta desde la que calificas el <b>Nivel de Servicio</b>. Las calificaciones de cada planta se promedian de forma simple. Solo aplica a los KPIs de ese pilar.
          </span>
        </div>
      )}

      {!canCaptureAllPillars && allowedPillars.length > 0 && (
        <div className="mb-5 sketch-border-soft bg-[var(--lala-blue-soft)] p-3 text-sm inline-flex items-start gap-2" data-testid="cm-scope-banner">
          <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--lala-blue)] pt-1">Tu pilar</span>
          <span>
            Solo puedes alimentar los pilares que Lala te asignó:{" "}
            <b className="text-[var(--lala-blue)]">
              {allowedPillars.map((p) => (PILLARS.find((x) => x.key === p)?.label || p)).join(" · ")}
            </b>. Otros pilares no aparecen en tus tarjetas.
          </span>
        </div>
      )}

      {mode === "proveedor" && (
      <div className="sketch-border bg-[var(--cream-soft)] p-4 mb-6 grid md:grid-cols-[1fr,1fr,1fr,auto] gap-3 items-end">
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">Periodo</div>
          <input type="month" value={period} max={max} onChange={(e) => setPeriod(e.target.value)}
            className="input-field w-full" data-testid="cm-period-input" />
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">Categoría</div>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            className="input-field w-full" data-testid="cm-category-select">
            <option value="all">Todas las categorías</option>
            {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block relative">
          <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">Buscar</div>
          <Search size={14} className="absolute left-3 top-9 text-[var(--ink-soft)]" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Nombre o SAP ID…"
            className="input-field w-full !pl-9" data-testid="cm-search-input" />
        </label>
        <div className="flex gap-2">
          <button onClick={() => selectAllVisible(true)}
            className="text-xs px-3 py-2 border border-[var(--lala-blue)] text-[var(--lala-blue)] rounded-lg hover:bg-white"
            data-testid="cm-select-all">
            <Users size={12} className="inline mr-1" /> Todos
          </button>
          <button onClick={() => selectAllVisible(false)}
            className="text-xs px-3 py-2 border border-[var(--line)] text-[var(--ink-soft)] rounded-lg hover:bg-white"
            data-testid="cm-clear-selection">
            Limpiar
          </button>
        </div>
      </div>
      )}

      <div className="sticky top-2 z-10 bg-white/95 backdrop-blur border border-[var(--line)] rounded-xl p-3 mb-6 flex flex-wrap items-center justify-between gap-3"
        data-testid="cm-action-bar">
        <div className="text-sm">
          <span className="font-semibold">{filtered.length}</span> proveedores visibles ·{" "}
          <span className="font-semibold">{Object.keys(expanded).filter((k) => expanded[k]).length}</span> expandidos ·{" "}
          <span className="font-semibold text-[var(--lala-blue)]">{pendingCount}</span> cambios pendientes
        </div>
        <div className="flex items-center gap-3">
          {globalMsg && <span className="text-sm text-green-700 inline-flex items-center gap-1" data-testid="cm-global-msg"><CheckCircle2 size={14} />{globalMsg}</span>}
          <button onClick={saveAll} disabled={busy}
            className="btn-primary text-sm inline-flex items-center gap-2"
            data-testid="cm-save-all-btn">
            <Save size={14} /> Guardar todo ({pendingCount})
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 inline-flex items-center gap-2" data-testid="cm-error">
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {mode === "proveedor" && (
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center text-[var(--ink-soft)] py-16" data-testid="cm-empty">
            No hay proveedores para la selección actual.
          </div>
        )}
        {filtered.map((s) => {
          const isOpen = expanded[s.id];
          const isSelected = !!selected[s.id];
          const m = matrix[s.id];
          return (
            <div key={s.id}
              className={`sketch-border bg-white overflow-hidden transition-shadow ${isSelected ? "ring-2 ring-[var(--lala-blue)]" : ""}`}
              data-testid={`cm-supplier-card-${s.id}`}>
              <button onClick={() => toggleExpand(s.id)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[var(--cream-soft)] transition-colors"
                data-testid={`cm-toggle-${s.id}`}>
                <div className="flex items-center gap-3 text-left">
                  <input type="checkbox" checked={isSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setSelected((prev) => ({ ...prev, [s.id]: e.target.checked }))}
                    className="accent-[var(--lala-blue)] w-4 h-4"
                    data-testid={`cm-check-${s.id}`} />
                  {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--lala-blue)] font-bold">
                      {s.category} {s.sap_id ? `· SAP ${s.sap_id}` : ""}
                    </div>
                    <div className="font-display text-lg">{s.name}</div>
                  </div>
                </div>
                <div className="text-xs text-[var(--ink-soft)]">
                  {m ? `${m.total_indicators} indicadores` : "cargar…"}
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-[var(--line)] px-4 py-4 bg-[var(--cream-soft)]">
                  {!m ? (
                    <div className="text-sm text-[var(--ink-soft)]">Cargando indicadores…</div>
                  ) : (
                    <SupplierPanel data={m} sid={s.id} period={period}
                      onEdit={(indCode, field, val) => editCell(s.id, indCode, field, val)}
                      dirty={dirty} savedAt={savedAt} isProposer={isProposer} plantSel={plantSel}
                      allowedPillars={canCaptureAllPillars ? null : allowedPillars} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {mode === "pilar" && (
        <PillarFirstView period={period} setPeriod={setPeriod} max={max}
          category={category} setCategory={setCategory} q={q} setQ={setQ}
          categoryOptions={categoryOptions}
          matrix={matrix} loadMatrix={loadMatrix} isProposer={isProposer}
          editCell={editCell} dirty={dirty} savedAt={savedAt} plantSel={plantSel} />
      )}
    </div>
  );
}

function PillarFirstView({ period, setPeriod, max, category, setCategory, q, setQ, categoryOptions,
  matrix, loadMatrix, editCell, dirty, savedAt, isProposer, plantSel }) {
  const [prog, setProg] = useState(null);
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(false);

  const refresh = () => {
    setLoading(true);
    http.get("/captura-mensual/progress", { params: { period } })
      .then((r) => setProg(r.data))
      .catch(() => setProg(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    setOpen({});
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const toggle = async (pk, sid) => {
    const key = `${pk}::${sid}`;
    const isOpen = open[key];
    setOpen((prev) => ({ ...prev, [key]: !isOpen }));
    if (!isOpen) await loadMatrix(sid);
  };

  const pctColor = (pct) => pct == null ? "#A5A399" : pct >= 90 ? "#166534" : pct >= 70 ? "#1E40AF" : pct >= 40 ? "#92400E" : "#991B1B";

  return (
    <div data-testid="cm-pillar-view">
      <div className="sketch-border bg-[var(--cream-soft)] p-4 mb-6 flex flex-wrap items-end gap-4">
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">Periodo</div>
          <input type="month" value={period} max={max} onChange={(e) => setPeriod(e.target.value)}
            className="input-field" data-testid="cm-pv-period-input" />
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">Categoría</div>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            className="input-field" data-testid="cm-pv-category-select">
            <option value="all">Todas las categorías</option>
            {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block relative">
          <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">Buscar</div>
          <Search size={14} className="absolute left-3 top-9 text-[var(--ink-soft)]" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Nombre o SAP ID…"
            className="input-field !pl-9" data-testid="cm-pv-search-input" />
        </label>
        <button onClick={refresh}
          className="text-xs px-3 py-2 border border-[var(--lala-blue)] text-[var(--lala-blue)] rounded-lg hover:bg-white"
          data-testid="cm-pv-refresh">Actualizar avance</button>
        {loading && <span className="text-xs text-[var(--ink-soft)]">Calculando…</span>}
      </div>

      {(prog?.pillars || []).map((pg) => {
        const rows = pg.suppliers.filter((s) => matchesFilters(s, category, q));
        const isFiltered = rows.length !== pg.suppliers.length;
        return (
        <div key={pg.pillar} className="mb-8" data-testid={`cm-pv-pillar-${pg.pillar}`}>
          <div className="font-display text-2xl text-[var(--lala-blue)] mb-1">Pilar · {pg.label}</div>
          <div className="text-xs text-[var(--ink-soft)] mb-3" data-testid={`cm-pv-count-${pg.pillar}`}>
            {isFiltered ? `${rows.length} de ${pg.suppliers.length}` : pg.suppliers.length} proveedores · captura el nivel Real de cada indicador de este pilar
          </div>
          {rows.length === 0 && pg.suppliers.length > 0 && (
            <div className="text-sm text-[var(--ink-soft)] italic py-2" data-testid={`cm-pv-filter-empty-${pg.pillar}`}>
              Ningún proveedor de este pilar coincide con los filtros.
            </div>
          )}
          <div className="space-y-2">
            {rows.map((s) => {
              const key = `${pg.pillar}::${s.id}`;
              const isOpen = open[key];
              const m = matrix[s.id];
              return (
                <div key={s.id} className="sketch-border bg-white overflow-hidden">
                  <button onClick={() => toggle(pg.pillar, s.id)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[var(--cream-soft)] transition-colors"
                    data-testid={`cm-pv-supplier-${pg.pillar}-${s.id}`}>
                    <div className="flex items-center gap-3 text-left">
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-[var(--lala-blue)] font-bold">
                          {s.category} {s.sap_id ? `· SAP ${s.sap_id}` : ""}
                        </div>
                        <div className="font-display text-lg">{s.name}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 w-[180px] shrink-0">
                      <div className="flex-1 h-2 rounded-full bg-[var(--cream-deep)]">
                        <div className="h-2 rounded-full" style={{ width: `${s.pct ?? 0}%`, background: pctColor(s.pct) }} />
                      </div>
                      <span className="text-xs font-bold" style={{ color: pctColor(s.pct) }}
                        data-testid={`cm-pv-pct-${pg.pillar}-${s.id}`}>
                        {s.expected ? `${s.captured}/${s.expected}` : "n/a"}
                      </span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-[var(--line)] px-4 py-4 bg-[var(--cream-soft)]">
                      {!m ? (
                        <div className="text-sm text-[var(--ink-soft)]">Cargando indicadores…</div>
                      ) : (
                        <SupplierPanel data={m} sid={s.id} period={period}
                          onEdit={(indCode, field, val) => editCell(s.id, indCode, field, val)}
                          dirty={dirty} savedAt={savedAt} isProposer={isProposer} plantSel={plantSel}
                          allowedPillars={[pg.pillar]} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        );
      })}
      {prog && (prog.pillars || []).length === 0 && (
        <div className="text-center text-[var(--ink-soft)] py-16" data-testid="cm-pv-empty">Sin pilares asignados.</div>
      )}
    </div>
  );
}

const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function formatNextDue(nextDue, period) {
  if (!nextDue) return null;
  const [y, m] = nextDue.split("-").map(Number);
  let inMonths = null;
  try {
    const [py, pm] = period.split("-").map(Number);
    inMonths = (y - py) * 12 + (m - pm);
  } catch { /* noop */ }
  return `${MONTH_NAMES[m - 1]} ${y}${inMonths ? ` (en ${inMonths} mes${inMonths === 1 ? "" : "es"})` : ""}`;
}

function NotDueRow({ ind, period }) {
  const last = ind.last_capture;
  const due = formatNextDue(ind.next_due, period);
  return (
    <div className="rounded-lg border border-dashed border-[var(--line)] bg-[#F7F5EF] px-3 py-2 opacity-75"
      data-testid={`cm-notdue-${ind.indicator_code}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-[var(--ink-soft)]">
          <span className="font-semibold">{ind.indicator_code}</span> · {ind.indicator_label}
          <span className="ml-2 text-[10px] uppercase tracking-wider font-bold">{PERIODICITY_LABEL[ind.periodicity] || ind.periodicity}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {last && (
            <span className="text-[11px] text-[var(--ink-soft)]" data-testid={`cm-notdue-last-${ind.indicator_code}`}>
              Última captura ({last.period}): Obj {last.objetivo_level ?? "—"} / Real {last.real_level ?? "—"}
            </span>
          )}
          <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-300 rounded-full px-2 py-0.5 inline-flex items-center gap-1"
            data-testid={`cm-notdue-chip-${ind.indicator_code}`}>
            <CheckCircle2 size={10} /> Al corriente{due ? ` · Próxima evaluación: ${due}` : ""}
          </span>
        </div>
      </div>
    </div>
  );
}

function SupplierPanel({ data, sid, period, onEdit, dirty, savedAt, allowedPillars, isProposer, plantSel }) {
  const { byPillar, skipped } = useMemo(() => {
    const map = {};
    let skipped = 0;
    for (const ind of data.indicators) {
      if (allowedPillars && !allowedPillars.includes(ind.pillar)) continue;
      const applies = ind.applies_in_period ?? periodicityApplies(ind.periodicity, period);
      if (!applies) skipped += 1;
      map[ind.pillar] = map[ind.pillar] || {};
      const critKey = ind.criterion_code || "SIN";
      map[ind.pillar][critKey] = map[ind.pillar][critKey] || { code: critKey, label: ind.criterion_label, indicators: [] };
      map[ind.pillar][critKey].indicators.push({ ...ind, _notDue: !applies });
    }
    return { byPillar: map, skipped };
  }, [data, allowedPillars, period]);

  const pillarKeys = Object.keys(byPillar);
  const [openPillars, setOpenPillars] = useState(() => {
    if (allowedPillars) return Object.fromEntries(pillarKeys.map((k) => [k, true]));
    return pillarKeys.length === 1 ? { [pillarKeys[0]]: true } : {};
  });
  const togglePillar = (pk) => setOpenPillars((prev) => ({ ...prev, [pk]: !prev[pk] }));

  if (Object.keys(byPillar).length === 0) {
    return (
      <div className="text-sm text-[var(--ink-soft)] italic py-4" data-testid={`cm-no-pillars-${sid}`}>
        No tienes pilares asignados para capturar en este proveedor. Contacta al admin si crees que es un error.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {skipped > 0 && (
        <div className="text-xs text-[var(--ink-soft)] bg-white border border-[var(--line)] rounded-lg px-3 py-2 inline-flex items-center gap-2"
          data-testid={`cm-periodicity-note-${sid}`}>
          <Calendar size={12} className="text-[var(--lala-blue)]" />
          {skipped} indicador{skipped === 1 ? "" : "es"} al corriente: no se capturan en {period} por su periodicidad
          y conservan su última calificación (aparecen en gris con su próxima fecha).
        </div>
      )}
      {Object.entries(byPillar).map(([pillarKey, crits]) => {
        const isOpen = !!openPillars[pillarKey];
        const nInd = Object.values(crits).reduce((a, c) => a + c.indicators.length, 0);
        const nCaptured = Object.values(crits).reduce((a, c) => a + c.indicators.filter((i) => !i._notDue && (i.capture?.real_level || i.proposal?.status === "pending")).length, 0);
        const nDue = Object.values(crits).reduce((a, c) => a + c.indicators.filter((i) => !i._notDue).length, 0);
        return (
        <div key={pillarKey} className="bg-white rounded-lg border border-[var(--line)] overflow-hidden" data-testid={`cm-pillar-${sid}-${pillarKey}`}>
          <button onClick={() => togglePillar(pillarKey)}
            className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-[var(--cream-soft)] transition-colors"
            data-testid={`cm-pillar-toggle-${sid}-${pillarKey}`}>
            <span className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-[var(--lala-blue)]">
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              Pilar · {PILLAR_LABEL[pillarKey] || pillarKey}
            </span>
            <span className={`text-[11px] font-bold ${nDue > 0 && nCaptured >= nDue ? "text-emerald-700" : "text-[var(--ink-soft)]"}`}
              data-testid={`cm-pillar-progress-${sid}-${pillarKey}`}>
              {nDue > 0 ? `${nCaptured}/${nDue} capturados` : `${nInd} al corriente`}
            </span>
          </button>
          {isOpen && (
          <div className="space-y-2 px-3 pb-3">
            {Object.values(crits).map((c) => (
              <div key={c.code} className="bg-[var(--cream-soft)] rounded-lg border border-[var(--line)] p-3">
                <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-2">
                  {c.code} · {c.label}
                </div>
                <div className="space-y-3">
                  {c.indicators.map((ind) => (
                    ind._notDue
                      ? <NotDueRow key={ind.indicator_code} ind={ind} period={period} />
                      : <IndicatorRow key={ind.indicator_code} ind={ind}
                          onEdit={onEdit} isProposer={isProposer}
                          dirtyKey={`${sid}::${ind.indicator_code}`}
                          dirty={dirty} savedAt={savedAt} plantSel={plantSel} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

// El texto visible nunca depende del servidor mientras se edita: el backend lo normaliza
// (strip) y responde con retraso, y eso pisaba lo que el usuario estaba escribiendo.
function NotesInput({ code, serverNotes, draftNotes, onEdit }) {
  const hasDraft = draftNotes !== undefined;
  const [text, setText] = useState(hasDraft ? draftNotes : serverNotes);
  const focusedRef = useRef(false);

  // Solo se reacciona a cambios reales del servidor o del borrador, nunca al foco ni a
  // que el borrador se vacíe tras guardar (el servidor aún puede traer el valor viejo).
  useEffect(() => {
    if (!focusedRef.current && !hasDraft) setText(serverNotes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverNotes]);
  useEffect(() => {
    if (!focusedRef.current && hasDraft) setText(draftNotes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftNotes]);

  return (
    <input type="text" value={text}
      onChange={(e) => { setText(e.target.value); onEdit(code, "notes", e.target.value); }}
      onFocus={() => { focusedRef.current = true; }}
      onBlur={() => { focusedRef.current = false; }}
      placeholder="Comentario interno (opcional)"
      className="input-field w-full text-xs"
      data-testid={`cm-notes-${code}`} />
  );
}

function IndicatorRow({ ind, onEdit, dirtyKey, dirty, savedAt, isProposer, plantSel }) {
  const cap = ind.capture || {};
  const prop = ind.proposal;
  const pendingProp = prop && prop.status === "pending" ? prop : null;
  const editValue = dirty[dirtyKey] || {};
  const objetivo = editValue.objetivo_level ?? pendingProp?.objetivo_level ?? cap.objetivo_level ?? "";
  // Nivel de Servicio con varias plantas y sin planta elegida: el Real es un promedio de solo lectura.
  // Con planta elegida se edita el valor de esa planta, no el promedio.
  const realView = plantRealView(cap, plantSel);
  const realIsAverage = realView.kind === "average";
  const real = realIsAverage ? realView.value : (editValue.real_level ?? pendingProp?.real_level ?? realView.value);
  const serverNotes = pendingProp?.notes ?? cap.notes ?? "";
  const isSaved = !!savedAt[dirtyKey];
  const isDirty = !!dirty[dirtyKey];

  const levels = ind.levels || [];
  const rubricFor = (n) => (levels.find((l) => l.score === n) || {}).label || "";
  const gap = levelGap(real, objetivo);

  return (
    <div className="grid md:grid-cols-[1fr,220px,220px] gap-3" data-testid={`cm-ind-${ind.indicator_code}`}>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-semibold flex items-center gap-2">
          {ind.indicator_code}
          {ind.periodicity && ind.periodicity !== "mensual" && (
            <span className="px-1.5 py-0.5 rounded-full bg-[var(--lala-blue-soft)] text-[var(--lala-blue)] font-bold normal-case tracking-normal"
              data-testid={`cm-periodicity-${ind.indicator_code}`}>
              {PERIODICITY_LABEL[ind.periodicity] || ind.periodicity}
            </span>
          )}
        </div>
        <div className="font-display text-base leading-tight">{ind.indicator_label}</div>
        {ind.description && (
          <div className="text-xs text-[var(--ink-soft)] mt-1">{ind.description}</div>
        )}
        {ind.scoring_method && (
          <div className="text-[10px] text-[var(--ink-soft)] mt-1 italic">Método · {ind.scoring_method}</div>
        )}
      </div>

      <LevelDropdown label="Objetivo" icon={<Target size={11} />}
        value={objetivo} onChange={(v) => onEdit(ind.indicator_code, "objetivo_level", v)}
        rubricFor={rubricFor} testid={`cm-obj-${ind.indicator_code}`} />

      {realIsAverage ? (
        <PlantAverageReal label="Real" icon={<Activity size={11} />}
          value={realView.value} plantCount={realView.count}
          hint="Elige una planta arriba para capturar su Real."
          testid={`cm-real-${ind.indicator_code}`} />
      ) : (
        <LevelDropdown label="Real" icon={<Activity size={11} />}
          value={real} onChange={(v) => onEdit(ind.indicator_code, "real_level", v)}
          rubricFor={rubricFor} testid={`cm-real-${ind.indicator_code}`} />
      )}

      <div className="md:col-span-3">
        <RubricPanel levels={levels} selected={realIsAverage ? "" : real} defaultOpen={false}
          onPick={realIsAverage ? undefined : (n) => onEdit(ind.indicator_code, "real_level", n)}
          testid={`cm-rubric-${ind.indicator_code}`} />
      </div>

      <div className="md:col-span-3">
        <NotesInput code={ind.indicator_code} serverNotes={serverNotes}
          draftNotes={editValue.notes} onEdit={onEdit} />
        <div className="flex items-center justify-between mt-1">
          <div className="text-[10px] text-[var(--ink-soft)]">
            {cap.updated_at ? (
              <>Última: {new Date(cap.updated_at).toLocaleString("es-MX")} {cap.updated_by ? `· ${cap.updated_by}` : ""}</>
            ) : "Sin captura previa"}
          </div>
          <div className="flex items-center gap-3">
            {pendingProp && !isDirty && (
              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5"
                data-testid={`cm-proposal-pending-${ind.indicator_code}`}>
                Cambio de Objetivo solicitado por {pendingProp.proposed_by} · en revisión del admin
              </span>
            )}
            {prop && prop.status === "rejected" && !isDirty && (
              <span className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-300 rounded-full px-2 py-0.5"
                data-testid={`cm-proposal-rejected-${ind.indicator_code}`}>
                Cambio de Objetivo rechazado{prop.review_note ? `: "${prop.review_note}"` : ""}
              </span>
            )}
            {Array.isArray(cap?.plants) && cap.plants.length > 0 && (
              <span className="inline-flex flex-wrap items-center gap-1" data-testid={`cm-plants-${ind.indicator_code}`}>
                <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-50 border border-emerald-300 rounded-full px-2 py-0.5">
                  Promedio simple · {cap.plants.length} planta{cap.plants.length > 1 ? "s" : ""}
                </span>
                {cap.plants.map((p) => (
                  <span key={p.plant} className="text-[9px] font-semibold text-[var(--ink-soft)] bg-white border border-[var(--line)] rounded-full px-1.5 py-0.5"
                    data-testid={`cm-plant-chip-${ind.indicator_code}-${p.plant}`}>
                    {p.plant}: {p.real_level ?? "—"}
                  </span>
                ))}
              </span>
            )}
            {real !== "" && objetivo !== "" && (
              <span className={`text-[11px] font-bold ${gap >= 0 ? "text-emerald-700" : "text-amber-700"}`}
                data-testid={`cm-gap-${ind.indicator_code}`}>
                gap: {gap > 0 ? "+" : ""}{gap}
              </span>
            )}
            {isDirty && <span className="text-[10px] text-amber-700">● pendiente</span>}
            {isSaved && (
              <span className="text-[10px] text-emerald-700 inline-flex items-center gap-0.5">
                <CheckCircle2 size={10} /> {isProposer ? "propuesta enviada" : "autoguardado"}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LevelDropdown({ label, icon, value, onChange, rubricFor, testid }) {
  const num = value === "" ? null : Number(value);
  const currentRubric = num ? rubricFor(num) : "";
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold flex items-center gap-1">
        {icon} {label}
      </div>
      <select value={value} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className="input-field text-sm font-bold"
        style={num ? { color: LEVEL_COLORS[num], borderColor: LEVEL_COLORS[num] } : {}}
        data-testid={testid}>
        <option value="">Sin capturar</option>
        {[1, 2, 3, 4].map((n) => (
          <option key={n} value={n}>{`${LEVEL_LABEL[n]} — ${String(rubricFor(n)).slice(0, 60)}${rubricFor(n).length > 60 ? "…" : ""}`}</option>
        ))}
      </select>
      {currentRubric && (
        <div className="text-[10px] text-[var(--ink-soft)] leading-tight border-l-2 pl-2"
          style={{ borderColor: LEVEL_COLORS[num] }}
          data-testid={`${testid}-rubric`}>
          {currentRubric}
        </div>
      )}
    </div>
  );
}
